import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HelpCircle, ChevronDown, Sparkles, Truck, RefreshCcw, Leaf, Shield, Gift, Heart } from 'lucide-react';

const CATEGORIES = [
  {
    id: 'shipping',
    label: 'Shipping',
    icon: Truck,
    items: [
      {
        q: 'How long will my order take to arrive?',
        a: 'We hand-pack every order in our Eureka apothecary within 1-3 business days. Standard shipping typically arrives in 3-7 business days after that, and Express in 1-3. You&apos;ll get a tracking link the moment we hand it off to the carrier.',
      },
      {
        q: 'Do you ship outside the US?',
        a: 'Not just yet. We currently ship only within the United States (including PO boxes and APO/FPO addresses). Sign up for our newsletter to be the first to know when we open international shipping.',
      },
      {
        q: 'Do you ship CBD everywhere?',
        a: 'We ship our hemp-derived CBD products to all US states where they are legal. If a product isn&apos;t available at your address, checkout will let you know and we won&apos;t charge you for it.',
      },
      {
        q: 'How much does shipping cost?',
        a: 'You&apos;ll see the exact rate at checkout &mdash; it depends on the size of your order and how quickly you want it. See our full Shipping Policy for details.',
      },
    ],
  },
  {
    id: 'returns',
    label: 'Returns & Refunds',
    icon: RefreshCcw,
    items: [
      {
        q: 'What is your return policy?',
        a: 'Because our products are handcrafted, perishable and consumable, we handle each return with care. If anything arrives damaged, incorrect, or just isn&apos;t right, email us within 14 days of delivery and we&apos;ll make it good &mdash; a replacement, a refund, or store credit, whichever feels right to you.',
      },
      {
        q: 'My package arrived damaged. What do I do?',
        a: 'We&apos;re so sorry. Snap a quick photo and email us at connectwithanherbalist@outlook.com within 7 days and we&apos;ll get a replacement out to you right away, no return shipping needed.',
      },
      {
        q: 'Can I change or cancel my order?',
        a: 'We start prepping orders quickly &mdash; if you catch us within 2 hours we can usually still make changes. Just reply to your order confirmation email or reach out and we&apos;ll do our best.',
      },
    ],
  },
  {
    id: 'ingredients',
    label: 'Ingredients & Safety',
    icon: Leaf,
    items: [
      {
        q: 'Are your products organic and natural?',
        a: 'Yes. We source organic and ethically-grown botanicals whenever possible and never use synthetic fragrance, parabens, or fillers. Every ingredient is listed on the product page so you can read exactly what you&apos;re bringing home.',
      },
      {
        q: 'Are your products vegan and cruelty-free?',
        a: 'We are cruelty-free across the board. Most of our line is vegan &mdash; a few items contain beeswax or honey from small family apiaries. That&apos;s noted clearly on each product page.',
      },
      {
        q: 'Do you test on animals?',
        a: 'Never. We test on ourselves, our family, and willing friends who ask us for &ldquo;a little something&rdquo; for what ails them.',
      },
      {
        q: 'I have allergies. What should I look out for?',
        a: 'Please read the full ingredient list on each product page before ordering. Common botanicals to watch for include tree nut oils, essential oils, and certain herbs. If you&apos;re unsure, email us &mdash; we&apos;ll happily walk you through it.',
      },
    ],
  },
  {
    id: 'cbd',
    label: 'CBD Questions',
    icon: Shield,
    items: [
      {
        q: 'Will your CBD products get me high?',
        a: 'No. Our CBD is hemp-derived and contains less than 0.3% THC, in compliance with the 2018 Farm Bill. You&apos;ll feel calm, grounded and supported &mdash; not intoxicated.',
      },
      {
        q: 'Will CBD show up on a drug test?',
        a: 'Full-spectrum CBD contains trace amounts of THC. While unlikely, it&apos;s possible to trigger a positive test. If you&apos;re subject to drug testing, please consult your healthcare provider before use.',
      },
      {
        q: 'How much CBD should I take?',
        a: 'Everyone is different. We recommend starting with the lowest suggested serving on the label and giving your body a few days to respond before adjusting. Consistency matters more than dose &mdash; the ritual is the medicine.',
      },
      {
        q: 'Is your CBD third-party tested?',
        a: 'Yes. Every batch is tested by an independent lab for potency and purity. Certificates of analysis are available upon request &mdash; just email us.',
      },
    ],
  },
  {
    id: 'usage',
    label: 'Storage & Dosage',
    icon: Sparkles,
    items: [
      {
        q: 'How should I store my products?',
        a: 'Keep elixirs, syrups and oils in a cool, dark place away from direct sunlight. A pantry shelf or bedside drawer is perfect. Body scrubs and lip balms can live in the bathroom &mdash; just keep the lid tight.',
      },
      {
        q: 'How long do your products last?',
        a: 'Because we make everything in small batches without harsh preservatives, most products are best within 6-12 months of opening. Each label carries a best-by date. Fresher is better &mdash; that&apos;s the whole point.',
      },
      {
        q: 'When is the best time of day to take an elixir?',
        a: 'Whenever it fits your ritual. Calming and sleep blends shine in the evening; Boost and Focus blends love the morning. Save a ritual on our Ritual Calendar and we&apos;ll gently remind you.',
      },
    ],
  },
  {
    id: 'gifts',
    label: 'Gift Cards & Gifts',
    icon: Gift,
    items: [
      {
        q: 'Do you sell gift cards?',
        a: 'Yes! Digital gift cards are available from $10 to $100 and are delivered by email &mdash; a lovely, no-guesswork way to share the ritual.',
      },
      {
        q: 'Can I include a gift note with my order?',
        a: 'Absolutely. Just leave a note in the cart before checkout and we&apos;ll hand-write it on a card and tuck it inside.',
      },
      {
        q: 'Do you offer wholesale or spa partnerships?',
        a: 'We do work with a small number of aligned partners. Reach out at connectwithanherbalist@outlook.com and tell us about your space &mdash; we&apos;d love to hear from you.',
      },
    ],
  },
  {
    id: 'account',
    label: 'Rituals & Account',
    icon: Heart,
    items: [
      {
        q: 'How do saved rituals work?',
        a: 'Save any ritual from a product or recipe page and we&apos;ll add it to your personal Ritual Calendar. Log daily to build a streak, and opt in to our Weekly Digest for a gentle reminder each Sunday.',
      },
      {
        q: 'How do I unsubscribe from emails?',
        a: 'Every marketing email has a one-click unsubscribe at the bottom. Transactional emails (order confirmations, shipping updates) will still come through so you can track your order.',
      },
      {
        q: 'Do I need an account to check out?',
        a: 'Nope &mdash; you can check out as a guest with just your email. Creating an account lets you save rituals, track streaks and see your order history in one place.',
      },
    ],
  },
];

const AccordionItem = ({ q, a, isOpen, onToggle, index }) => (
  <div className="border-b border-[#e5decf]" data-testid={`faq-item-${index}`}>
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-6 py-6 text-left group"
      aria-expanded={isOpen}
      data-testid={`faq-question-${index}`}
    >
      <span className="font-serif-elegant text-xl md:text-2xl text-[#2f2a24] group-hover:text-[#3a4a3b] transition-colors">
        {q}
      </span>
      <ChevronDown
        className={`w-5 h-5 text-[#7a8b6b] shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
      />
    </button>
    <div
      className={`grid transition-all duration-300 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100 pb-8' : 'grid-rows-[0fr] opacity-0'}`}
    >
      <div className="overflow-hidden">
        <p
          className="text-[#4a4640] leading-relaxed"
          data-testid={`faq-answer-${index}`}
          dangerouslySetInnerHTML={{ __html: a }}
        />
      </div>
    </div>
  </div>
);

const FAQ = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [activeCategory, setActiveCategory] = useState(CATEGORIES[0].id);
  const [openIndex, setOpenIndex] = useState(0);

  const category = CATEGORIES.find(c => c.id === activeCategory);

  return (
    <div data-testid="faq-page">
      {/* Hero */}
      <section className="bg-gradient-to-b from-[#efe9db] to-[#faf7f1]">
        <div className="max-w-4xl mx-auto px-6 py-16 md:py-24 text-center">
          <div className="inline-flex items-center gap-2 uppercase tracking-[0.35em] text-xs text-[#7a8b6b] mb-4">
            <HelpCircle className="w-4 h-4" /> We&apos;re here to help
          </div>
          <h1 className="font-serif-elegant text-5xl md:text-6xl mb-4">Ask an Herbalist</h1>
          <p className="text-[#4a4640] leading-relaxed max-w-2xl mx-auto">
            Little answers to the questions we hear most &mdash; from shipping and CBD basics to
            storing your elixirs and building a daily ritual. Still curious?{' '}
            <a
              href="mailto:connectwithanherbalist@outlook.com"
              className="underline underline-offset-4"
              data-testid="faq-email-link"
            >
              Write to us
            </a>{' '}
            &mdash; we love a good chat.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
        <div className="grid md:grid-cols-[220px_1fr] gap-10 lg:gap-16">
          {/* Category rail */}
          <aside className="md:sticky md:top-24 md:self-start">
            <div className="uppercase tracking-[0.35em] text-xs text-[#7a8b6b] mb-4">Topics</div>
            <nav className="flex md:flex-col gap-2 overflow-x-auto md:overflow-visible pb-2 md:pb-0" data-testid="faq-category-nav">
              {CATEGORIES.map(cat => {
                const Icon = cat.icon;
                const active = cat.id === activeCategory;
                return (
                  <button
                    key={cat.id}
                    onClick={() => { setActiveCategory(cat.id); setOpenIndex(0); }}
                    className={`flex items-center gap-3 px-4 py-3 text-left text-sm whitespace-nowrap transition-colors ${
                      active
                        ? 'bg-[#3a4a3b] text-[#f6f2ea]'
                        : 'text-[#4a4640] hover:bg-[#efe9db]'
                    }`}
                    data-testid={`faq-category-${cat.id}`}
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <span>{cat.label}</span>
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Questions */}
          <div>
            <h2 className="font-serif-elegant text-3xl md:text-4xl mb-2">{category.label}</h2>
            <div className="h-px w-16 bg-[#7a8b6b] mb-6" />
            <div>
              {category.items.map((item, i) => (
                <AccordionItem
                  key={item.q}
                  q={item.q}
                  a={item.a}
                  index={i}
                  isOpen={openIndex === i}
                  onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#3a4a3b] text-[#f6f2ea]">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <div className="uppercase tracking-[0.35em] text-xs text-[#dcd6c7] mb-3">Still have a question?</div>
          <h3 className="font-serif-elegant text-3xl md:text-4xl mb-6">We&apos;d love to hear from you.</h3>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:connectwithanherbalist@outlook.com"
              className="btn-outline !border-[#dcd6c7] !text-[#dcd6c7] hover:!bg-[#dcd6c7] hover:!text-[#3a4a3b]"
              data-testid="faq-contact-email-btn"
            >
              Email an Herbalist
            </a>
            <Link
              to="/about"
              className="btn-outline !border-[#dcd6c7] !text-[#dcd6c7] hover:!bg-[#dcd6c7] hover:!text-[#3a4a3b]"
              data-testid="faq-about-link"
            >
              Meet the Team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
